import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Module-level cache shared across all TTSButton instances.
// Key: `${lang}::${text}` → { url, blobUrl }. Persists during the SPA session.
// This eliminates the round-trip on repeated plays (critical on 3G/4G in Conakry).
const ttsUrlCache = new Map();      // text_key → azure url (persists per session)
const ttsBlobCache = new Map();     // azure url → object URL of prefetched blob

async function fetchAndCacheBlob(url) {
  if (ttsBlobCache.has(url)) return ttsBlobCache.get(url);
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    ttsBlobCache.set(url, blobUrl);
    return blobUrl;
  } catch {
    // Fallback: return the remote url; the <audio> tag will stream it
    return url;
  }
}

/**
 * useTTS – shared TTS hook with browser cache.
 * Returns { state, error, play, stop } where state ∈ 'idle' | 'loading' | 'playing'.
 * @param {string} text – text to synthesize (name + description concatenated by caller)
 */
export function useTTS(text) {
  const { i18n } = useTranslation();
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const audioRef = useRef(null);

  const lang = (i18n?.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); a.currentTime = 0; } catch (_) { /* ignore */ }
    }
    setState("idle");
  }, []);

  const play = useCallback(async () => {
    if (state === "playing") {
      stop();
      return;
    }
    if (state === "loading") return;

    const t = (text || "").trim();
    if (t.length < 2) return;

    const key = `${lang}::${t}`;
    setError("");
    setState("loading");

    try {
      let url = ttsUrlCache.get(key);
      if (!url) {
        const res = await fetch(`${API_BASE}/api/tts/speak`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: t, lang }),
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => ({}))).detail;
          throw new Error(detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        url = data?.url;
        if (!url) throw new Error("Audio indisponible");
        ttsUrlCache.set(key, url);
      }

      // Prefer prefetched blob URL (0ms subsequent play, less bandwidth on 3G)
      const src = await fetchAndCacheBlob(url);
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => {
        setError("Lecture impossible");
        setState("idle");
      };
      await audio.play();
      setState("playing");
    } catch (e) {
      setError(e.message || "Erreur TTS");
      setState("idle");
    }
  }, [text, lang, state, stop]);

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        try { a.pause(); } catch (_) { /* ignore */ }
      }
    };
  }, []);

  return { state, error, play, stop };
}
